import type { I18n } from '@learnforge/i18n';

export interface MountHelpOptions {
  lineKeys: readonly string[];
  i18n: I18n<string>;
}

export interface HelpHandle {
  destroy(): void;
}

const QUESTION_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="8" cy="8" r="6.5"/>
  <path d="M6.2 6.2 a1.8 1.8 0 0 1 3.6 0 c0 .9 -.6 1.2 -1 1.5 c-.4 .3 -.8 .6 -.8 1.3"/>
  <circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none"/>
</svg>`;

/**
 * Mounts a help-toggle button + collapsible panel containing translated
 * instruction lines. The button sits between the lang-picker and the
 * theme-toggle in the top-right. Engine-agnostic — plain DOM.
 *
 * State is owned internally: toggling the button shows/hides the panel.
 * Clicking outside or pressing Escape also closes the panel.
 */
export function mountHelp(opts: MountHelpOptions): HelpHandle {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'help-toggle';
  btn.setAttribute('aria-label', 'Help');
  btn.setAttribute('aria-expanded', 'false');
  btn.innerHTML = QUESTION_SVG;
  document.body.appendChild(btn);

  const panel = document.createElement('div');
  panel.className = 'help-panel';
  panel.setAttribute('data-open', 'false');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Help');
  document.body.appendChild(panel);

  const renderPanel = (): void => {
    panel.innerHTML = '';
    for (const key of opts.lineKeys) {
      const line = document.createElement('div');
      line.className = 'help-line';
      line.textContent = opts.i18n.t(key);
      panel.appendChild(line);
    }
  };
  renderPanel();
  const unsubscribe = opts.i18n.subscribe(() => renderPanel());

  let open = false;
  const setOpen = (next: boolean): void => {
    open = next;
    panel.setAttribute('data-open', open ? 'true' : 'false');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const onBtnClick = (e: MouseEvent): void => {
    e.stopPropagation();
    setOpen(!open);
  };

  const onDocClick = (e: MouseEvent): void => {
    if (!open) return;
    const target = e.target as Node | null;
    if (target && (panel.contains(target) || btn.contains(target))) return;
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && open) {
      setOpen(false);
      btn.focus();
    }
  };

  btn.addEventListener('click', onBtnClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKeyDown);

  return {
    destroy(): void {
      btn.removeEventListener('click', onBtnClick);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      unsubscribe();
      btn.remove();
      panel.remove();
    },
  };
}
